import PropTypes from 'prop-types';
import { createContext, useEffect, useRef, useState } from 'react';
import mainAxios from 'axios';
// third-party
import { Chance } from 'chance';
import jwtDecode from 'jwt-decode';

// reducer - state management
import { AUTH_ACTIONS } from 'config';
// import authReducer from 'store/reducers/auth';

// project import
import Loader from 'components/Loader';
import authAxios from 'utils/authAxios';
import axios from 'utils/axios';
import { getSSORefreshTokenUrl, getSSOAuthUrl, getSSOInvalidateTokenUrl, compareObjects } from 'utils/helpers';
import { useLocation } from 'react-router-dom';
// import { dispatch as storeDispatch } from 'store';
import { useSelector, dispatch } from 'store';
import { setLogin, updateStatus, updatePermission, setPermission } from 'store/actions/auth';

const chance = new Chance();

// constant
// const initialState = {
//   isLoggedIn: false,
//   isInitialized: false,
//   user: null,
//   loginStatus: null,
//   permission: [],
//   currentUser: {}
// };

const verifyToken = (serviceToken) => {
  if (!serviceToken) {
    return false;
  }
  const decoded = jwtDecode(serviceToken);
  /**
   * Property 'exp' does not exist on type '<T = unknown>(token: string, options?: JwtDecodeOptions | undefined) => T'.
   */
  return decoded.exp * 1000 - 300000 > Date.now();
};

const setSession = (serviceToken) => {
  if (serviceToken) {
    localStorage.setItem('serviceToken', serviceToken);
    authAxios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  } else {
    localStorage.removeItem('serviceToken');
    delete authAxios.defaults.headers.common.Authorization;
    delete axios.defaults.headers.common.Authorization;
  }
};

const getOrgsFromRoles = (roles) => {
  let orgs = [];
  roles.map((p) => {
    if (p.application_id == 2 && !orgs.find((o) => o.id === p.organization_id)) orgs.push({ id: p.organization_id, name: p.company_name });
  });
  return orgs;
};
// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
  const { search } = useLocation();
  const auth = useSelector((state) => state.auth);
  const stateObj = useRef(auth);
  const [decodedToken, setDecodedToken] = useState(null);
  // let intervalTemp = undefined;
  useEffect(() => {
    stateObj.current = auth;
  }, [auth]);

  const updateLoginStatus = (status = null) => {
    dispatch(
      updateStatus({
        loginStatus: status
      })
    );
  };

  const init = async () => {
    let storedOrg = localStorage.getItem('currentOrg');
    if (storedOrg) {
      let parsedOrg = JSON.parse(storedOrg);
      if (parsedOrg.id !== stateObj.current?.currentOrg?.id) {
        dispatch({
          type: AUTH_ACTIONS.UPDATE_AUTH,
          payload: {
            currentOrg: JSON.parse(storedOrg)
          }
        });
      }
    }
    try {
      const qParams = new URLSearchParams(search);
      const token = qParams.get('code');
      const serviceToken = window.localStorage.getItem('serviceToken');
      // const serviceToken = currentUser?.token;
      // const persistedPermissions = window.localStorage.getItem('permission');
      // console.log('persistedPermissions', persistedPermissions)

      const userDetails = window.localStorage.getItem('userDetails');

      if (token && !decodedToken) {
        const decoded = jwtDecode(token);
        !userDetails &&
          window.localStorage.setItem('userDetails', JSON.stringify({ name: decoded?.homeAccountId?.name, email: decoded?.email }));
        setDecodedToken(decoded);
      }

      if (stateObj.current?.permission) {
        let currentUser = localStorage.getItem('currentUser');
        currentUser = currentUser ? JSON.parse(currentUser) : null;
        if (!compareObjects(stateObj.current?.permission, currentUser?.permissions)) {
          dispatch(
            updatePermission({
              permission: stateObj.current?.permission
            })
          );
        }
      }

      if (serviceToken && verifyToken(serviceToken)) {
        // dispatch login true
        setSession(serviceToken);
        if (!compareObjects(userDetails, stateObj.current.user) || stateObj.current.isLoggedIn != true) {
          dispatch(
            setLogin({
              isLoggedIn: true,
              loginStatus: null,
              user: userDetails ? JSON.parse(userDetails) : {}
            })
          );
        }
      } else if (serviceToken && !verifyToken(serviceToken)) {
        updateLoginStatus('Checking your token');
        try {
          const response = await mainAxios.get(getSSORefreshTokenUrl(), {
            headers: {
              Authorization: `Bearer ${serviceToken}`
            }
          });
          if (response.data && response.data?.token) {
            setSession(response.data?.token);
            dispatch(
              setLogin({
                isLoggedIn: true,
                user: userDetails ? JSON.parse(userDetails) : {}
              })
            );
          } else {
            updateLoginStatus('Invalid token');
            logout();
          }
        } catch (error) {
          console.error('SSO refresh token failed:', error);
          updateLoginStatus('SSO service unavailable');
          // Instead of logging out immediately, you could retry or handle gracefully
          logout();
        }
      } else if (token) {
        loginWithToken(token);
      } else {
        // if invalid or no token - redirect to microsoft
        updateLoginStatus(null);
        logout();
      }
    } catch (e) {
      updateLoginStatus(null);
      logout();
    }
  };

  // useEffect(() => {
  //   updateLoginStatus('Trying to login..');
  //   init();
  //   // if (intervalTemp) clearInterval(intervalTemp);
  //   // intervalTemp = setInterval(() => {
  //   //   init();
  //   // }, 60000);
  // }, []);
  let intervalTemp = undefined;
  useEffect(() => {
    updateLoginStatus('Trying to login..');
    if (intervalTemp) {
      clearInterval(intervalTemp);
    } else {
      init();
    }
    intervalTemp = setInterval(() => {
      init();
    }, 60000);
  }, []);

  const validateAuthToken = async (token) => {
    const response = await authAxios.get('/api/v1/auth/signin', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    setSession(token);
    // window.localStorage.setItem('permission', JSON.stringify(response.data.permission));
    dispatch(setPermission({ permission: response.data.permission }));
    dispatch(
      setLogin({
        isLoggedIn: true,
        user: {}
      })
    );
    dispatch(
      updatePermission({
        permission: response.data.permission
      })
    );
  };

  const login = async (email, password) => {
    const response = await authAxios.post('/uaa/oauth/token/login', {
      userName: email,
      password: password
    });
    const { access_token, user = {} } = response.data;
    setSession(access_token);
    dispatch(
      setLogin({
        isLoggedIn: true,
        user
      })
    );
  };

  const loginWithMicrosoft = () => {
    updateLoginStatus('Redirecting you to login page..');
    window.location.href = getSSOAuthUrl();
  };

  const loginWithToken = async (token) => {
    updateLoginStatus('Validating your token');
    try {
      const response = await axios.get(`/api/v1/auth/signin`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (response.data.status) {
        console.log('response', response);
        let orgs = getOrgsFromRoles(response.data.roles);
        let currentOrg = orgs.find((org) => org.id == response.data.employee_organization_id);
        setSession(response.data.serviceToken);
        window.localStorage.setItem('serviceToken', response.data.serviceToken);
        window.localStorage.setItem('currentUser', JSON.stringify(response.data));
        window.localStorage.setItem('orgs', JSON.stringify(orgs));
        window.localStorage.setItem('currentOrg', JSON.stringify(currentOrg));
        window.localStorage.setItem('settings', JSON.stringify(response.data.settings));
        window.localStorage.setItem(
          'currentRole',
          response.data.roles.find((r) => r.organization_id == currentOrg.id && r.application_id == 2)?.role_name
        );
        // window.localStorage.setItem('permission', JSON.stringify(response.data.permission));
        dispatch(setPermission({ permission: response.data.permission }));
        dispatch({
          type: AUTH_ACTIONS.SET_CURRENT_USER,
          payload: {
            currentUser: {
              id: response.data.id,
              sso_id: response.data.sso_id,
              client_name: response.data.client_name,
              db_name: response.data.db_name,
              defaultStatePointer: response.data.defaultStatePointer,
              default_payer: response.data.default_payer,
              isFirstTimeLogin: response.data.isFirstTimeLogin,
              name: response.data.name,
              payer: response.data.payer,
              perms: response.data.perms,
              token: response.data.token
            }
          }
        });
        dispatch(
          setLogin({
            isLoggedIn: true,
            user: window.localStorage.getItem('userDetails') ?? {}
          })
        );
        dispatch({
          type: AUTH_ACTIONS.UPDATE_AUTH,
          payload: {
            permission: response.data.permissions,
            orgs: orgs,
            roles: response.data.roles,
            currentOrg: currentOrg,
            serviceToken: response.data.serviceToken,
            settings: response.data.settings
          }
        });
      }
      return response.data;
    } catch (e) {
      console.error(e);
      updateLoginStatus('Invalid token');
    }
  };

  const register = async (email, password, firstName, lastName) => {
    // todo: this flow need to be recode as it not verified
    const id = chance.bb_pin();
    const response = await authAxios.post('/api/account/register', {
      id,
      email,
      password,
      firstName,
      lastName
    });
    let users = response.data;

    if (window.localStorage.getItem('users') !== undefined && window.localStorage.getItem('users') !== null) {
      const localUsers = window.localStorage.getItem('users');
      users = [
        ...JSON.parse(localUsers),
        {
          id,
          email,
          password,
          name: `${firstName} ${lastName}`
        }
      ];
    }

    window.localStorage.setItem('users', JSON.stringify(users));
  };

  const logout = async () => {
    const token = window.localStorage.getItem('serviceToken');
    try {
      if (token) {
        await axios.get(getSSOInvalidateTokenUrl(), {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      }
    } catch (err) {
      console.log('SSO invalidate token failed:', err);
      // Continue with logout even if SSO service is down
    }
    setSession(null);
    window.localStorage.removeItem('users');
    window.localStorage.removeItem('serviceToken');
    window.localStorage.removeItem('currentUser');
    window.localStorage.removeItem('selectedOptions');
    window.localStorage.removeItem('permission');
    window.localStorage.removeItem('persist:root');
    window.localStorage.removeItem('userType');
    window.localStorage.removeItem('splashShow');
    window.localStorage.removeItem('abc');
    window.localStorage.removeItem('orgs');
    window.localStorage.removeItem('currentOrg');
    window.localStorage.removeItem('currentRole');
    window.localStorage.removeItem('settings');

    dispatch({ type: AUTH_ACTIONS.LOGOUT });
  };

  const resetPassword = async () => {};

  const updateProfile = () => {};

  return (
    <>
      {(stateObj.current?.loginStatus && stateObj.current?.isInitialized !== undefined && !stateObj.current?.isInitialized) ||
      stateObj.current?.loading ? (
        <Loader />
      ) : null}
      <JWTContext.Provider
        value={{
          ...stateObj.current,
          decodedToken,
          login,
          loginWithMicrosoft,
          validateAuthToken,
          loginWithToken,
          logout,
          register,
          resetPassword,
          updateProfile
        }}
      >
        {children}
      </JWTContext.Provider>
    </>
  );
};

JWTProvider.propTypes = {
  children: PropTypes.node
};

export default JWTContext;
